import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Card, Flex, JanePayLogo, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import { DealTag } from '../DealTag/DealTag';

import styles from './PaymentOptionCard.module.css';

export interface PaymentOptionCardProps {
  ariaLabel?: string;
  cashBack?: number;
  feeAmount?: number;
  name: string;
  nameSubLabel?: ReactNode;
  onClick: () => void;
  selected: boolean;
  showCashbackBadge?: boolean;
  showDefaultFeeLabel?: boolean;
  showJanePayLogo?: boolean;
  total?: number;
}

export const PaymentOptionCard = ({
  ariaLabel,
  cashBack = 0,
  feeAmount = 0,
  name,
  nameSubLabel,
  onClick,
  selected,
  showCashbackBadge = false,
  showDefaultFeeLabel = false,
  showJanePayLogo = false,
  total,
}: PaymentOptionCardProps) => {
  const payByBankUI = useFlag(FLAGS.c3PayByBankUI);
  const showFee = feeAmount > 0;
  const showBadge = showCashbackBadge && cashBack > 0;

  return (
    <Card
      border={selected ? 'primary' : 'transparent-black-ten'}
      borderWidth={selected ? '2px' : '1px'}
      width="100%"
      flat
      style={{ padding: selected ? 0 : '1px' }}
    >
      <Card.Content
        ariaLabel={ariaLabel ?? `Select ${name}`}
        onClick={onClick}
        p={24}
        className={clsx(styles['paymentButton'], {
          [styles['paymentButton_withBadge']]: showBadge,
        })}
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          gap={4}
          grow={1}
          minWidth={0}
        >
          {payByBankUI ? (
            <>
              <Flex alignItems="center" gap={8}>
                <Typography variant="body-semibold">{name}</Typography>
                {cashBack > 0 && (
                  <DealTag
                    tagLabel={`${formatCurrency(cashBack)} cash back`}
                    isGold
                    maxLabelLength={20}
                  />
                )}
              </Flex>
              {nameSubLabel &&
                (typeof nameSubLabel === 'string' ? (
                  <Typography variant="small-body-regular">
                    {nameSubLabel}
                  </Typography>
                ) : (
                  nameSubLabel
                ))}
            </>
          ) : (
            <>
              {showJanePayLogo && (
                <JanePayLogo
                  height={20}
                  width={98}
                  data-testid="jane-pay-logo"
                />
              )}
              <Typography
                variant={
                  showJanePayLogo ? 'small-body-regular' : 'body-semibold'
                }
                className={showJanePayLogo ? styles['bankName'] : undefined}
              >
                {name}
              </Typography>
            </>
          )}
        </Flex>
        <Flex flexDirection="column" alignItems="flex-end" gap={4} shrink={0}>
          {total && (
            <Typography variant="body-semibold">
              {formatCurrency(total)}
            </Typography>
          )}
          {payByBankUI && showJanePayLogo && (
            <JanePayLogo height={20} width={82} data-testid="jane-pay-logo" />
          )}
          {(showFee || showDefaultFeeLabel) && (
            <Typography
              variant="small-body-regular"
              color="grays-mid"
              whiteSpace="nowrap"
            >
              {showFee
                ? `Incl. ${formatCurrency(feeAmount)} fee`
                : 'Store fees vary'}
            </Typography>
          )}
          {!payByBankUI && showBadge && (
            <DealTag
              tagLabel={`+${formatCurrency(cashBack)} cash back`}
              isGold
              maxLabelLength={20}
            />
          )}
        </Flex>
      </Card.Content>
    </Card>
  );
};
